import axios from "axios";
import ApiService from "./apiService";

class ProspectsService {
  static throwIfResponseError(result, fallbackMessage = "Ocurrió un error.") {
    const code = Number(result?.code || result?.statusCode || result?.status || result?.data?.status || 0);
    const hasWrappedApiError = [1, true, "1", "true"].includes(result?.data?.error);
    const hasExplicitFailure =
      result?.success === false ||
      result?.data?.success === false ||
      result?.data?.error_detalle?.success === false;

    if (code >= 400 || hasWrappedApiError || hasExplicitFailure) {
      if (code === 413) {
        const error = new Error("La base es demasiado grande para enviarla en una sola carga. Probá nuevamente; se enviará por lotes.");
        error.response = { data: { ...result, resp: error.message } };
        throw error;
      }

      const detail = result?.resp || result?.data?.error_detalle || result?.data?.error || result?.data?.message || fallbackMessage;
      const message = typeof detail === "string"
        ? detail
        : detail?.error || detail?.message || JSON.stringify(detail);
      const error = new Error(message || fallbackMessage);
      error.response = { data: { ...result, resp: message || fallbackMessage } };
      throw error;
    }
  }

  // ── Legacy ────────────────────────────────────────────────────────────────
  static async getCampaingns() {
    try {
      const result = await axios.get('/prospects/getCampaingns');
      return result.data;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  static async getSubsidiary() {
    try {
      const result = await axios.get('/prospects/getSubsidiary');
      return result.data;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // ── Campaigns ─────────────────────────────────────────────────────────────
  static async getAllCampaigns() {
    const result = await ApiService.get("/prospects/campaigns");
    return result;
  }

  static async getAllCampaignTypes() {
    const result = await ApiService.get("/prospects/campaign-types");
    return result;
  }

  static async getAllSpeechs() {
    const result = await ApiService.get("/prospects/speechs");
    return result;
  }

  static async createSpeech(data) {
    const result = await ApiService.post("/prospects/speechs", data);
    return result;
  }

  static async updateSpeech(id, data) {
    const result = await ApiService.put(`/prospects/speechs/${id}`, data);
    return result;
  }

  static async deleteSpeech(id) {
    const result = await ApiService.delete(`/prospects/speechs/${id}`);
    return result;
  }

  static async createCampaign(data) {
    const result = await ApiService.post("/prospects/campaigns", data);
    return result;
  }

  static async updateCampaign(id, data) {
    const result = await ApiService.put(`/prospects/campaigns/${id}`, data);
    return result;
  }

  static async deleteCampaign(id) {
    const result = await ApiService.delete(`/prospects/campaigns/${id}`);
    return result;
  }

  // ── Answer Options ────────────────────────────────────────────────────────
  static async getAllOptions() {
    const result = await ApiService.get("/prospects/options");
    return result;
  }

  static async createOption(data) {
    const result = await ApiService.post("/prospects/options", data);
    return result;
  }

  static async updateOption(id, data) {
    const result = await ApiService.put(`/prospects/options/${id}`, data);
    return result;
  }

  static async deleteOption(id) {
    const result = await ApiService.delete(`/prospects/options/${id}`);
    return result;
  }

  // ── Option ↔ Campaign Assignment ──────────────────────────────────────────
  static async getOptionsByCampaign(id_campana) {
    const result = await ApiService.get(`/prospects/campaigns/${id_campana}/options`);
    return result;
  }

  static async addOptionToCampaign(id_campana, id_opcion) {
    const result = await ApiService.post("/prospects/campaigns/assign-option", { id_campana, id_opcion });
    return result;
  }

  static async removeOptionFromCampaign(id_campana, id_opcion) {
    const result = await ApiService.delete(`/prospects/campaigns/${id_campana}/options/${id_opcion}`);
    return result;
  }

  // ── Automatic Agenda ─────────────────────────────────────────────────────
  static async getAutomaticAgenda(campaignId = null) {
    const suffix = campaignId ? `?campaignId=${campaignId}` : "";
    const result = await ApiService.get(`/prospects/automatic-agenda${suffix}`);
    return result;
  }

  static async saveAutomaticAgenda(data) {
    const result = await ApiService.post("/prospects/automatic-agenda", data);
    return result;
  }

  static async deleteAutomaticAgenda(id) {
    const result = await ApiService.delete(`/prospects/automatic-agenda/${id}`);
    return result;
  }

  static async runAutomaticAgenda() {
    const result = await ApiService.post("/prospects/automatic-agenda/run", {});
    return result;
  }

  static async runPaymentReconciliation(data) {
    const result = await ApiService.post("/prospects/payment-reconciliation/run", data);
    this.throwIfResponseError(result, "No se pudo ejecutar la conciliación de pagos.");
    return result;
  }

  // ── Limpieza de datos ───────────────────────────────────────────────────
  static async getContactsCleanupSummary() {
    const result = await ApiService.get("/prospects/parameters/contacts-cleanup/summary");
    return result;
  }

  static async deleteContactsData(data) {
    const result = await ApiService.post("/prospects/parameters/contacts-cleanup/delete", data);
    this.throwIfResponseError(result, "No se pudieron borrar los datos de contactos.");
    return result;
  }

  // ── Areas y Colaboradores ─────────────────────────────────────────────────
  static async getAvailableProspectUsers() {
    const result = await ApiService.get("/prospects/prospect-users/available");
    return result;
  }

  static async getProspectUsers() {
    const result = await ApiService.get("/prospects/prospect-users");
    return result;
  }

  static async syncProspectUsers(userIds) {
    const result = await ApiService.post("/prospects/prospect-users/sync", { userIds });
    return result;
  }

  // ── Asignar / Desasignar Prospectos ───────────────────────────────────────
  static async getAssignmentCampaigns() {
    const result = await ApiService.get("/prospects/assignments/campaigns");
    return result;
  }

  static async getAssignmentPriorities(campaignId) {
    const result = await ApiService.get(`/prospects/assignments/campaigns/${campaignId}/priorities`);
    return result;
  }

  static async updateAssignmentActivePriorities(campaignId, priorities) {
    const result = await ApiService.put(`/prospects/assignments/campaigns/${campaignId}/priorities`, { priorities });
    return result;
  }

  static async getAssignmentBranches(campaignId) {
    const suffix = campaignId ? `?campaignId=${campaignId}` : "";
    const result = await ApiService.get(`/prospects/assignments/branches${suffix}`);
    return result;
  }

  static async unassignProspectsByUser(campaignId, prospectUserId) {
    const result = await ApiService.post(`/prospects/assignments/campaigns/${campaignId}/users/${prospectUserId}/unassign`, {});
    return result;
  }

  static async transferProspectsByUser(campaignId, prospectUserId, targetProspectUserId) {
    const result = await ApiService.post(`/prospects/assignments/campaigns/${campaignId}/users/${prospectUserId}/transfer`, {
      targetProspectUserId,
    });
    return result;
  }

  static async assignProspectsByBranch(campaignId, sucursal, priorities) {
    const result = await ApiService.post(`/prospects/assignments/campaigns/${campaignId}/assign`, { sucursal, priorities });
    return result;
  }

  static async assignProspectsByAvailableBranches(campaignId, priorities) {
    const result = await ApiService.post(`/prospects/assignments/campaigns/${campaignId}/assign-by-branches`, { priorities });
    return result;
  }

  static async assignProspectsByUsers(campaignId, prospectUserIds, priorities, quantityPerUser) {
    const body = { prospectUserIds, priorities };
    if (quantityPerUser) body.quantityPerUser = quantityPerUser;
    const result = await ApiService.post(`/prospects/assignments/campaigns/${campaignId}/assign-users`, body);
    return result;
  }

  static async crossBranchAssign(campaignId, { sourceSucursal, targetSucursal, prospectUserIds, priorities, limit }) {
    const result = await ApiService.post(`/prospects/assignments/campaigns/${campaignId}/cross-branch-assign`, {
      sourceSucursal,
      targetSucursal,
      prospectUserIds,
      priorities,
      limit,
    });
    return result;
  }

  static async unassignProspectsByBranch(campaignId, sucursal) {
    const result = await ApiService.post(`/prospects/assignments/campaigns/${campaignId}/unassign`, { sucursal });
    return result;
  }

  // ── Gestión Prospectos ───────────────────────────────────────────────────
  static async getManagementQueue(userId, campaignId = null, search = "", page = 1, pageSize = 25) {
    const query = new URLSearchParams({ userId });
    if (campaignId) query.append("campaignId", campaignId);
    if (search) query.append("search", search);
    query.append("page", page);
    query.append("pageSize", pageSize);
    const result = await ApiService.get(`/prospects/management/queue?${query.toString()}`);
    return result;
  }

  static async getManagementProspectDetail(prospectId, userId) {
    const result = await ApiService.get(`/prospects/management/prospects/${prospectId}?userId=${userId}`);
    return result;
  }

  static async saveManagement(prospectId, data) {
    const result = await ApiService.post(`/prospects/management/prospects/${prospectId}`, data);
    this.throwIfResponseError(result, "No se pudo guardar la gestión.");
    return result;
  }

  static async bulkImportManagements(rows, apply = false) {
    const result = await ApiService.post("/prospects/management/bulk-import", {
      rows,
      apply,
    });
    this.throwIfResponseError(result, "No se pudo procesar la carga masiva de gestiones.");
    return result;
  }

  static async getManagementDashboard() {
    const result = await ApiService.get("/prospects/management/dashboard");
    return result;
  }

  static async recycleManagementByCampaign(campaignId, optionIds) {
    const result = await ApiService.post(`/prospects/management/dashboard/campaigns/${campaignId}/recycle`, { optionIds });
    return result;
  }

  static async getManagementReports(params = {}) {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        query.append(key, value);
      }
    });

    const suffix = query.toString() ? `?${query.toString()}` : "";
    const result = await ApiService.get(`/prospects/management/reports${suffix}`);
    return result;
  }

  static async getManagementReportAgents(params = {}) {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        query.append(key, value);
      }
    });

    const suffix = query.toString() ? `?${query.toString()}` : "";
    const result = await ApiService.get(`/prospects/management/reports/agents${suffix}`);
    return result;
  }

  // ── Prospect Base ─────────────────────────────────────────────────────────
  static async getBaseBranches() {
    const result = await ApiService.get("/prospects/base/branches");
    return result;
  }

  static async uploadBase({ campanaId, registros, prospectUserId }) {
    const result = await ApiService.post("/prospects/base", { campanaId, registros, prospectUserId });
    this.throwIfResponseError(result, "No se pudo cargar la base de prospectos.");
    return result;
  }
}

export default ProspectsService;
